import { z } from 'zod';

const id = z.string().min(1).max(160);
const fence = { episodeId: id, requestId: id };
/** Owner identity and host authority are supplied by the authenticated room service. */
export const GenTVRuntimeCommandV2Schema = z.discriminatedUnion('type', [
  z.object({ ...fence, type: z.literal('begin_v2'), expectedCreationRevision: z.number().int().nonnegative() }).strict(),
  z.object({ ...fence, type: z.literal('choose_move_v2'), roundId: id, claimId: id,
    expectedOptionsRevision: z.number().int().nonnegative(), optionId: id }).strict(),
  z.object({ ...fence, type: z.literal('control_program_v2'), expectedProgramRevision: z.number().int().nonnegative(),
    action: z.enum(['pause', 'resume', 'skip', 'end']), segmentId: id.optional(), decisionConfirmed: z.boolean().optional() }).strict(),
  z.object({ ...fence, type: z.literal('set_photo_reference_v2'), characterId: id, claimId: id,
    expectedCharacterRevision: z.number().int().nonnegative(), image: z.string().max(500_000).nullable() }).strict(),
  z.object({ ...fence, type: z.literal('rate_episode_v2'), rating: z.number().int().min(1).max(5) }).strict(),
  z.object({ ...fence, type: z.literal('new_episode_v2'), reusePremise: z.boolean() }).strict(),
]);
export type GenTVRuntimeCommandV2 = z.infer<typeof GenTVRuntimeCommandV2Schema>;
export interface GenTVPublicRuntimeV2 {
  status: 'running' | 'finished' | 'ended';
  programRevision: number;
  paused: boolean;
  roundNumber: 1 | 2;
  objective: string;
  /** Only the segment currently on air. Generated-scene text cannot pre-announce its result. */
  currentSegment?: { segmentId: string; kind: string; text?: string; fallback: boolean };
  /** The planned program so far, in order: kind and status only, never text, so it cannot pre-announce a result. */
  segments?: { segmentId: string; kind: string; status: string }[];
  round: { roundId: string; status: 'preparing' | 'open' | 'locked' | 'resolved'; deadlineAt?: number; submittedCount: number; participantCount: number };
  recap: { eventId: string; summary: string }[];
  outcome?: { kind: 'favorable' | 'partial' | 'adverse'; explanation: string };
  interruptedReason?: 'host_end' | 'channel_unavailable' | 'participants_disconnected' | 'options_unavailable';
}
export interface GenTVPrivateRuntimeV2 {
  roundId: string;
  roundNumber: 1 | 2;
  status: 'preparing' | 'open' | 'locked' | 'resolved';
  optionsRevision: number;
  claimId: string;
  options: { optionId: string; label: string; context?: string; intendedAction: string; benefit: string; cost: string;
    contested: boolean; risk?: { die: 6; threshold: 3 | 4 | 5; successFaces: number; description: string } }[];
  selectedOptionId: string | null;
  paused: boolean;
  remainingMs: number;
  receipts: { actorId: string; resolutionId: string; status: 'succeeded' | 'partial' | 'blocked' | 'held' | 'awaiting_scene';
    /** Private effects are rendered as owner-only prose, never shared as an event recap. */
    privateUpdates: string[] }[];
}
